
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, MapPin, Building, Star, Phone, ArrowRight, Users, BarChart2, MessageCircle } from "lucide-react";
import CallToAction from "@/components/CallToAction";

const LocalSeo = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Local SEO Services in Los Angeles | Los Angeles SEO Group</title>
        <meta name="description" content="Dominate local search with our specialized Local SEO services in Los Angeles. Improve your Google Business Profile, local rankings, and drive neighborhood traffic." />
        <link rel="canonical" href="https://losangelesseogroup.com/services/local-seo" />
      </Helmet>

      <Navbar />

      <main className="flex-grow pt-28 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-background to-muted/30 py-16">
          <div className="page-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-sky-500/10 text-sky-500">
                  Local SEO Services
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-6">
                  Dominate <span className="text-gradient bg-sky-gradient">Local Search</span> in Your Neighborhood
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Focused strategies to improve local search visibility, drive neighborhood traffic, and attract customers in your community.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="bg-sky-gradient hover:opacity-90">
                    <Link to="/contact" className="flex items-center">
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/case-studies">
                      See Success Stories
                    </Link>
                  </Button>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-sky-gradient opacity-10 blur-xl rounded-full"></div>
                <div className="relative bg-card border border-border shadow-lg rounded-xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center">
                      <div className="bg-sky-500/10 p-2 rounded-lg mr-3">
                        <MapPin className="h-6 w-6 text-sky-500" />
                      </div>
                      <div>
                        <h3 className="font-bold">Los Angeles Plumbing Co.</h3>
                        <div className="flex items-center text-amber-400">
                          <Star className="h-4 w-4 fill-current" />
                          <Star className="h-4 w-4 fill-current" />
                          <Star className="h-4 w-4 fill-current" />
                          <Star className="h-4 w-4 fill-current" />
                          <Star className="h-4 w-4 fill-current" />
                          <span className="text-xs ml-1 text-muted-foreground">245 reviews</span>
                        </div>
                      </div>
                    </div>
                    <Phone className="h-5 w-5 text-sky-500" />
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm">
                      <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>Plumbing Services</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>15 mins away · 7.2 mi</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <MessageCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>"Fast service, fixed my emergency leak!"</span>
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 p-3 rounded-lg text-sm flex items-center justify-between">
                    <span className="font-medium">Found in local search</span>
                    <div className="flex items-center text-emerald-500">
                      <span className="text-xs mr-1">↑ 285%</span>
                      <BarChart2 className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Our Local SEO <span className="text-gradient bg-sky-gradient">Solutions</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Comprehensive strategies to improve your local online presence and connect with customers in your area
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Building className="h-6 w-6 text-sky-500" />,
                  title: "Google Business Profile Optimization",
                  description: "Full optimization of your business profile to improve local visibility and customer engagement."
                },
                {
                  icon: <MapPin className="h-6 w-6 text-sky-500" />,
                  title: "Local Keyword Research",
                  description: "Identify the most valuable location-based keywords your potential customers are searching for."
                },
                {
                  icon: <Star className="h-6 w-6 text-sky-500" />,
                  title: "Review Generation Strategy",
                  description: "Implement systems to collect, manage, and leverage customer reviews to build trust."
                },
                {
                  icon: <Users className="h-6 w-6 text-sky-500" />,
                  title: "Local Citation Building",
                  description: "Create consistent NAP (Name, Address, Phone) citations across key directories and platforms."
                },
                {
                  icon: <MessageCircle className="h-6 w-6 text-sky-500" />,
                  title: "Local Content Development",
                  description: "Create targeted content that resonates with your local audience and addresses their needs."
                },
                {
                  icon: <BarChart2 className="h-6 w-6 text-sky-500" />,
                  title: "Comprehensive Reporting",
                  description: "Detailed monthly reports tracking your local visibility, rankings, and traffic improvements."
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card border border-border rounded-xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="bg-sky-500/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-muted/30">
          <div className="page-container">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold mb-6">
                    Why Choose Our <span className="text-gradient bg-sky-gradient">Local SEO</span> Services
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    As a Los Angeles-based SEO company, we understand the local market and what it takes to stand out in competitive neighborhood searches.
                  </p>
                  
                  <ul className="space-y-4">
                    {[
                      "Deep understanding of local search factors",
                      "Neighborhood-specific optimization strategies",
                      "Proven results with local businesses",
                      "Transparent reporting and communication",
                      "Holistic approach to local online presence",
                      "Continuous optimization based on performance"
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 * index }}
                        className="flex items-start"
                      >
                        <CheckCircle className="h-5 w-5 text-sky-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-card border border-border rounded-xl overflow-hidden shadow-lg"
                >
                  <div className="bg-sky-gradient p-6 text-white">
                    <h3 className="text-xl font-bold mb-2">Local SEO Package</h3>
                    <div className="flex items-baseline mb-4">
                      <span className="text-3xl font-bold">$950</span>
                      <span className="text-white/80 ml-2">/ month</span>
                    </div>
                    <p className="text-white/80 text-sm">
                      Focused strategies to improve local search visibility and drive neighborhood traffic.
                    </p>
                  </div>
                  
                  <div className="p-6">
                    <ul className="space-y-3 mb-6">
                      {[
                        "Google Business Profile optimization",
                        "Local keyword research and targeting",
                        "Review generation strategy",
                        "Local citation building",
                        "Local content development",
                        "Competitor analysis",
                        "Monthly reporting"
                      ].map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-sky-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button asChild className="w-full bg-sky-gradient hover:opacity-90">
                      <Link to="/contact">Start Your Campaign</Link>
                    </Button>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Our <span className="text-gradient bg-sky-gradient">Local SEO</span> Process
              </h2>
              <p className="text-lg text-muted-foreground">
                A proven step-by-step approach to improve your local search visibility
              </p>
            </div>
            
            <div className="relative">
              <div className="absolute left-[50%] top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>
              
              <div className="space-y-12">
                {[
                  {
                    number: "01",
                    title: "Local Audit & Competitive Analysis",
                    description: "We analyze your current local presence, identify competitors, and discover opportunities for improvement."
                  },
                  {
                    number: "02",
                    title: "Optimization Strategy",
                    description: "We create a customized local SEO strategy targeting your specific neighborhood and business needs."
                  },
                  {
                    number: "03",
                    title: "Google Business Profile Enhancement",
                    description: "We fully optimize your GBP with the right categories, attributes, photos, and engaging content."
                  },
                  {
                    number: "04",
                    title: "Citation Building & Cleanup",
                    description: "We ensure your business information is accurate and consistent across all local directories."
                  },
                  {
                    number: "05",
                    title: "Review Acquisition & Management",
                    description: "We implement systems to generate reviews and respond appropriately to build reputation."
                  },
                  {
                    number: "06",
                    title: "Track & Adjust",
                    description: "We continuously monitor performance and adjust strategies to maximize your local visibility."
                  }
                ].map((step, index) => (
                  <div key={index} className="relative">
                    <div className={`md:absolute top-0 w-8 h-8 rounded-full bg-sky-gradient text-white flex items-center justify-center font-bold z-10 ${index % 2 === 0 ? 'md:left-[calc(50%-16px)]' : 'md:left-[calc(50%-16px)]'}`}>
                      {index + 1}
                    </div>
                    
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${index % 2 === 0 ? 'md:pr-16' : 'md:pl-16'}`}>
                      <motion.div
                        initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className={`bg-card border border-border rounded-xl p-6 ${index % 2 === 0 ? 'md:col-start-1' : 'md:col-start-2'}`}
                      >
                        <div className="flex items-center mb-4">
                          <div className="bg-sky-500/10 px-3 py-1 rounded text-sky-500 font-mono text-sm font-bold mr-3">
                            {step.number}
                          </div>
                          <h3 className="text-xl font-bold">{step.title}</h3>
                        </div>
                        <p className="text-muted-foreground">{step.description}</p>
                      </motion.div>
                      
                      {index % 2 === 0 ? <div></div> : <div></div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CallToAction className="max-w-5xl mx-auto mb-16" />
      </main>

      <Footer />
    </PageTransition>
  );
};

export default LocalSeo;
